import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { Vehicle } from '../../../shared/types/vehicle.types';
import useOdometerHistory from '../wrappers/use-odometer-history';
import useVehicle from '../wrappers/use-vehicle';
import { odometerModalStyles } from './OdometerModal.styles';
import { useTranslation } from 'react-i18next';

interface OdometerModalProps {
    machine: Vehicle | null;
    onClose: () => void;
    onSaved: (vehicleUid: string, odometer: { kilometers: number; motohours: number; recordedAt: Date }) => void;
}

const OdometerModal: React.FC<OdometerModalProps> = ({ machine, onClose, onSaved }) => {
    const { t } = useTranslation();
    const { create: createOdometerRecord } = useOdometerHistory();
    const { edit: editVehicle } = useVehicle();
    const [form, setForm] = useState({ date: '', km: '', mthHours: '' });

    useEffect(() => {
        if (machine) {
            setForm({ date: new Date().toISOString().split('T')[0], km: '', mthHours: '' });
        }
    }, [machine]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!machine) return;
        const kilometers = Number(form.km);
        const addedMth = Number(form.mthHours);
        const currentMotohours = machine.odometer?.motohours ?? 0;
        const totalMotohours = currentMotohours + addedMth;
        await createOdometerRecord({
            vehicleUid: machine.uid,
            recordedAt: new Date(form.date),
            kilometers,
            motohours: totalMotohours,
            addedMth,
        });
        await editVehicle(machine.uid, { odometer: { kilometers, motohours: totalMotohours } });
        onSaved(machine.uid, { kilometers, motohours: totalMotohours, recordedAt: new Date(form.date) });
        onClose();
    };

    return (
        <Dialog open={!!machine} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Typography fontWeight={600}>{t('odometerModal.title')}</Typography>
                <Typography variant="body2" color="text.secondary">{machine?.model}</Typography>
            </DialogTitle>
            <Box component="form" onSubmit={handleSubmit}>
                <DialogContent sx={odometerModalStyles.content}>
                    <Alert severity="info" sx={odometerModalStyles.infoAlert}>
                        {t('odometerModal.kmInfo')}<br />
                        {t('odometerModal.mthInfo')}
                    </Alert>
                    <TextField
                        label={t('common.date')}
                        type="date"
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                    <TextField
                        label={t('odometerModal.kmLabel')}
                        type="number"
                        required
                        fullWidth
                        InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment>, inputProps: { min: 0 } }}
                        placeholder={t('odometerModal.kmPlaceholder')}
                        value={form.km}
                        onChange={(e) => setForm({ ...form, km: e.target.value })}
                    />
                    <TextField
                        label={t('odometerModal.mthLabel')}
                        type="number"
                        fullWidth
                        InputProps={{ endAdornment: <InputAdornment position="end">hod</InputAdornment>, inputProps: { min: 0 } }}
                        placeholder="0"
                        value={form.mthHours}
                        onChange={(e) => setForm({ ...form, mthHours: e.target.value })}
                    />
                </DialogContent>
                <DialogActions sx={odometerModalStyles.actions}>
                    <Button onClick={onClose}>{t('common.cancel')}</Button>
                    <Button type="submit" variant="contained">{t('common.save')}</Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default OdometerModal;
